import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    plansService,
    type Plan as Product,
    type PlanPrice as Price,
    type PlansResponse,
    type Region,
} from "../../lib/api/plans";
import type { Subscription } from "../../lib/api/subscriptions";
import { useAlertStore } from "../../stores/alertStore";
import { SectionLoader } from "../LogoLoader";

interface PlansGridProps {
    subscription: Subscription | null;
    onSelectPlan: (plan: Product, priceId?: string) => void;
    publicCtaLabel?: string;
    /**
     * Whether the user is allowed to switch regions. Defaults to true for the
     * public marketing page (anonymous visitors browsing prices). Set to false
     * for authenticated flows — once a user has an account, switching regions
     * during checkout would lock them into a currency that doesn't match the
     * profile/country they signed up with.
     */
    allowRegionSwitch?: boolean;
    /**
     * When set, an empty result is treated as "website self-serve billing is
     * disabled" (plans are sold via the Shopify App Store) rather than "no plan
     * in this region": the grid shows a notice + link to this URL instead of the
     * generic empty state, and hides the region selector. Pass only from the
     * public pricing page; authenticated flows keep the plain empty state.
     */
    appStoreUrl?: string;
    /**
     * Notifies the parent whether any website plans exist, so it can adjust
     * surrounding copy (e.g. hide the marketing hero/footnote when billing is
     * disabled). Memoize the handler to avoid refetch loops.
     */
    onPlansResolved?: (hasPlans: boolean) => void;
}

export const PlansGrid = ({ subscription, onSelectPlan, publicCtaLabel, allowRegionSwitch = true, appStoreUrl, onPlansResolved }: PlansGridProps) => {
    const { t } = useTranslation("common");
    const { showAlert } = useAlertStore();

    const [plans, setPlans] = useState<Product[]>([]);
    const [availableRegions, setAvailableRegions] = useState<Region[]>([]);
    const [currentRegion, setCurrentRegion] = useState<Region | null>(null);
    const [regionOverride, setRegionOverride] = useState<string | null>(null);
    const [isLoadingBilling, setIsLoadingBilling] = useState(true);
    const [productIntervals, setProductIntervals] = useState<Record<string, "month" | "year">>({});
    const [isCurrencyLocked, setIsCurrencyLocked] = useState(false);
    const [billingDisabled, setBillingDisabled] = useState(false);

    const getCtaText = (plan: Product, isCurrent: boolean) => {
        if (subscription === null && publicCtaLabel) return publicCtaLabel;
        if (isCurrent) return t("plans.currentPlan");
        if (plan.name === "Free") return t("plans.downgradeToFree", "Switch to Free");
        if (plan.isHighlighted) return t("plans.upgradeTo", { planName: plan.name });
        return t("plans.getPlan", `Get ${plan.name}`, { planName: plan.name });
    };

    const fetchPlans = useCallback(async () => {
        setIsLoadingBilling(true);
        try {
            const response: PlansResponse = await plansService.getPlans(regionOverride || undefined);
            if (response.success) {
                setPlans(response.data);
                const hasPlans = response.data.length > 0;
                setBillingDisabled(!hasPlans);
                onPlansResolved?.(hasPlans);
                const regions = response.meta.availableRegions || [];
                setAvailableRegions(regions);
                setIsCurrencyLocked(!!response.meta.currencyLocked);

                const shownCode = regionOverride || response.meta.requestedCountry || response.meta.shownCountry;
                const shownRegion = regions.find((r) => r.code === shownCode)
                    || { code: response.meta.shownCountry, name: response.meta.shownCountry, currency: response.meta.shownCurrency };
                setCurrentRegion(shownRegion);

                const initialIntervals: Record<string, "month" | "year"> = {};
                response.data.forEach((p) => {
                    const defaultPrice = p.prices?.find((pr) => pr.isDefault);
                    if (defaultPrice) {
                        initialIntervals[p.id] = (defaultPrice.billing_interval || defaultPrice.interval) as "month" | "year";
                    } else {
                        const firstPrice = p.prices?.[0];
                        initialIntervals[p.id] = (firstPrice?.billing_interval || firstPrice?.interval || "month") as "month" | "year";
                    }
                });
                setProductIntervals(initialIntervals);
            }
        } catch (error) {
            console.error("Failed to fetch plans", error);
            showAlert({
                message: String(t("userErrors.subscription.unableToLoadPlans", "Unable to load plans.")),
                type: "error",
            });
        } finally {
            setIsLoadingBilling(false);
        }
    }, [regionOverride, showAlert, t, onPlansResolved]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    if (isLoadingBilling) {
        return (
            <div className="w-full max-w-7xl mx-auto px-6">
                <SectionLoader message={String(t("plans.loading"))} />
            </div>
        );
    }

    const currentProductId = subscription?.variant?.product?.id;
    // Hide the region selector when:
    //  - the caller doesn't allow region switching (authenticated flows), or
    //  - the user is already locked to a Stripe currency, or
    //  - there's nothing meaningful to switch to.
    const showRegionSelector =
        allowRegionSwitch && !isCurrencyLocked && !billingDisabled && availableRegions.length > 1;

    return (
        <div className="w-full max-w-7xl mx-auto px-6 flex flex-col">
            <div className={`flex flex-wrap justify-center gap-6 flex-1 ${showRegionSelector ? "pb-12" : "pb-6"}`}>
                {plans.length > 0 ? (
                    plans
                        .sort((a, b) => (a.displayOrder ?? 0.5) - (b.displayOrder ?? 0.5))
                        .map((plan) => {
                            const currentInterval = productIntervals[plan.id] || "month";
                            const prices = plan.prices || [];
                            const monthlyPrice = prices.find((p: Price) => p.billing_interval === "month");
                            const yearlyPrice = prices.find((p: Price) => p.billing_interval === "year");
                            const hasVariations = !!monthlyPrice && !!yearlyPrice;

                            const price = currentInterval === "month" ? monthlyPrice : yearlyPrice;
                            const displayPrice = price || prices[0];

                            const isCurrentProduct = plan.id === currentProductId;
                            const isCurrentPrice = subscription?.price?.id === displayPrice?.id;
                            const isCurrent = !!subscription && isCurrentProduct && (!displayPrice || isCurrentPrice);

                            const features = plan.features || [];

                            return (
                                <div
                                    key={plan.id}
                                    className={`group relative flex flex-col p-8 rounded-2xl bg-white dark:bg-neutral-900 w-full md:w-[350px] ${plan.isHighlighted === true
                                        ? "border-2 border-accent-primary shadow-xl scale-[1.02] z-10"
                                        : "border border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-xl"
                                        }`}
                                >
                                    {plan.isHighlighted && plan.highlightText && (
                                        <div className="absolute -top-4 inset-x-0 flex justify-center">
                                            <div className="bg-accent-primary text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md uppercase tracking-wide">
                                                {plan.highlightText}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex flex-col min-h-[60px] mb-6">
                                        <div className={`flex justify-end ${!hasVariations ? "hidden md:flex md:min-h-[32px] md:mb-6" : "min-h-[32px] mb-6"}`}>
                                            {hasVariations && (
                                                <div className="inline-flex items-center bg-neutral-100 dark:bg-neutral-800 rounded-full p-0.5">
                                                    <button
                                                        onClick={() => setProductIntervals((prev) => ({ ...prev, [plan.id]: "month" }))}
                                                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 whitespace-nowrap ${currentInterval === "month"
                                                            ? "bg-white dark:bg-neutral-700 shadow-sm text-black dark:text-white"
                                                            : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
                                                            }`}
                                                    >
                                                        {t("plans.monthly")}
                                                        {monthlyPrice?.offer && <span className="text-blue-600 dark:text-blue-400 font-normal">{monthlyPrice.offer}</span>}
                                                    </button>
                                                    <button
                                                        onClick={() => setProductIntervals((prev) => ({ ...prev, [plan.id]: "year" }))}
                                                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 whitespace-nowrap ${currentInterval === "year"
                                                            ? "bg-white dark:bg-neutral-700 shadow-sm text-black dark:text-white"
                                                            : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
                                                            }`}
                                                    >
                                                        {t("plans.yearly")}
                                                        {yearlyPrice?.offer && <span className="text-blue-600 dark:text-blue-400 font-normal">{yearlyPrice.offer}</span>}
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <h3 className="text-2xl font-bold text-black dark:text-white mb-2">{plan.name}</h3>
                                        <div className="min-h-[20px]">
                                            {plan.slogan && (
                                                <p className="text-sm font-medium text-black dark:text-neutral-300">{plan.slogan}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        {displayPrice ? (
                                            <div className="flex flex-col">
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-4xl font-bold text-black dark:text-white tracking-tight">
                                                        {new Intl.NumberFormat("en-US", {
                                                            style: "currency",
                                                            currency: displayPrice.currency,
                                                            minimumFractionDigits: 0,
                                                        }).format(displayPrice.amount / 100)}
                                                    </span>
                                                    <span className="text-neutral-500 dark:text-neutral-400 font-medium text-sm">
                                                        /{displayPrice.interval === "month" ? t("plans.perMonth") : t("plans.perYear")}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mt-1">
                                                    {displayPrice.billing_interval === "year" ? t("plans.billedAnnually") : t("plans.billedMonthly")}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-4xl font-bold text-black dark:text-white tracking-tight">{t("plans.freePrice")}</span>
                                        )}
                                    </div>

                                    {displayPrice?.trialDays && displayPrice.trialDays > 0 && !isCurrent ? (
                                        <div className="mb-4 flex items-center gap-2 text-sm text-green-600 dark:text-green-500 font-semibold bg-green-50 dark:bg-green-900/30 p-2 rounded-lg">
                                            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t("plans.trial", { days: displayPrice.trialDays, defaultValue: `${displayPrice.trialDays}-day free trial, cancel anytime.` })}
                                        </div>
                                    ) : null}

                                    <div className="mb-8">
                                        <button
                                            onClick={() => onSelectPlan(plan, displayPrice?.id)}
                                            className={`w-full py-3 px-6 rounded-full font-bold text-sm transition-colors shadow-sm cursor-pointer ${isCurrent
                                                ? "bg-white dark:bg-neutral-800 text-black dark:text-white border-2 border-black dark:border-white hover:bg-gray-50 dark:hover:bg-neutral-700"
                                                : "bg-black dark:bg-white text-white dark:text-black border-2 border-transparent hover:bg-gray-900 dark:hover:bg-gray-100"
                                                }`}
                                            disabled={isCurrent || (!displayPrice && !subscription && !publicCtaLabel)}
                                        >
                                            {getCtaText(plan, isCurrent)}
                                        </button>
                                    </div>

                                    <div className="flex-1 border-t border-neutral-100 dark:border-neutral-800 pt-4">
                                        <p className="text-black dark:text-white text-sm mb-3">
                                            {plan.description || t("plans.defaultDescription")}
                                        </p>
                                        <ul className="space-y-2">
                                            {features.map((feature: string, i: number) => (
                                                <li key={i} className="flex items-start gap-1 text-sm text-black dark:text-neutral-300">
                                                    <span className="w-5 h-5 text-black dark:text-white shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_8ba68f7b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8ba68f7b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                    <span className="leading-snug">{feature.replace(/^•\s*/, "")}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            );
                        })
                ) : billingDisabled && appStoreUrl ? (
                    <div className="col-span-full flex flex-col items-center justify-center gap-5 p-12 w-full text-center">
                        <p className="text-text-primary font-medium text-lg max-w-md">{t("plans.billingViaShopify")}</p>
                        <a
                            href={appStoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary inline-flex items-center gap-2"
                        >
                            {t("plans.getOnShopify")}
                            <span aria-hidden>→</span>
                        </a>
                    </div>
                ) : (
                    <div className="col-span-full flex items-center justify-center p-12 w-full">
                        <p className="text-neutral-400 dark:text-neutral-500 font-medium text-lg">{t("plans.noPlansAvailable")}</p>
                    </div>
                )}
            </div>

            {showRegionSelector && (
                <div className="pt-8 pb-12 border-t border-neutral-100 dark:border-neutral-800 flex justify-center">
                    <div className="flex items-center gap-2 text-xs text-neutral-400 dark:text-neutral-500">
                        <span>{t("plans.pricesFor")}</span>
                        <select
                            value={currentRegion?.code || ""}
                            onChange={(e) => setRegionOverride(e.target.value)}
                            className="bg-transparent border-none text-neutral-600 dark:text-neutral-400 font-medium cursor-pointer hover:text-neutral-800 dark:hover:text-neutral-200 focus:outline-none focus:ring-0 text-xs py-0 pr-5 appearance-none"
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0 center",
                                backgroundSize: "14px",
                            }}
                        >
                            {availableRegions.map((region) => (
                                <option key={region.code} value={region.code}>
                                    {region.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};
